import { useNodeUploader } from './use-node-uploader'
import type { TaskUpload, TaskUploadNode } from '../types'
import type { useTaskUploads } from './use-task-uploads'

export function useFileUploader(
  taskUploads: ReturnType<typeof useTaskUploads>,
) {
  const {
    getTaskUploadById,
    updateTaskUploadItemStatus,
    setTaskUploadNodeFile,
  } = taskUploads
  const { uploadFile } = useNodeUploader()

  function getTaskUploadItemFileParentId(
    taskUpload: TaskUpload,
    taskUploadFile: TaskUploadNode,
  ) {
    /*
     *  SECTION can be: 'inbox' or 'files'
     *  case 'inbox': do not need any parent
     *  case 'files':
     *      if 'parentNodeId' then will be uploaded in a nested folder
     *      otherwise in the "root" folder => 'documents'
     * */
    let parentNodeId = ''
    if (taskUpload.target.section === 'files') {
      const folderId = taskUploadFile?.data?.idFolder
      if (folderId) {
        const folderData = taskUpload?.folders?.[folderId]?.data
        if (folderData?.nodeId) {
          parentNodeId = folderData.nodeId ?? ''
        }
      } else {
        parentNodeId = taskUpload.target.node.nodeId ?? ''
      }
    }
    return parentNodeId
  }

  async function assertFileReadable(file: File) {
    try {
      await file.slice(0, 1).arrayBuffer()
    } catch {
      throw new Error('ERR_FILE_NOT_FOUND')
    }
  }

  async function onUploadFileItem(taskUploadId: number, fileIndex: number) {
    const taskUpload = getTaskUploadById(taskUploadId)
    if (!taskUpload || !taskUpload.files) return
    const fileNode = taskUpload.files[fileIndex]

    // mark processing
    setTaskUploadNodeFile(taskUploadId, fileIndex, {
      status: 'processing',
      progress: 0,
    })

    const parentNodeId = getTaskUploadItemFileParentId(taskUpload, fileNode)
    const abortController = new AbortController()
    setTaskUploadNodeFile(taskUploadId, fileIndex, { abortController })

    try {
      await assertFileReadable(fileNode.data?.file as File)
      const uploadedNode = await uploadFile({
        section: taskUpload.target.section,
        parentNodeId,
        file: fileNode.data?.file as File,
        typeId: taskUpload.target.typeId,
        boxId: taskUpload.target.boxId,
        onProgress: (percent, uploadedBytes, totalBytes) => {
          setTaskUploadNodeFile(taskUploadId, fileIndex, {
            progress: percent,
            data: {
              ...fileNode.data,
              uploadSize: {
                total: totalBytes ?? fileNode.data?.file?.size ?? 0,
                uploaded: uploadedBytes ?? 0,
              },
            },
          })
        },
        abortController,
      })

      // mark completed
      setTaskUploadNodeFile(taskUploadId, fileIndex, {
        status: 'completed',
        progress: 100,
        data: {
          ...fileNode.data,
          node: uploadedNode,
          uploadSize: { total: uploadedNode.size, uploaded: uploadedNode.size },
        },
      })

      // call the onSuccess callback
      if (fileNode.onSuccess) {
        await fileNode.onSuccess(taskUploadId, fileIndex)
      }
    } catch (err) {
      setTaskUploadNodeFile(taskUploadId, fileIndex, {
        status: 'error',
        data: { ...fileNode.data, error: err as Error },
      })
      // call the onSuccess callback
      if (fileNode.onFailure) {
        await fileNode.onFailure(taskUploadId, fileIndex)
      }
    } finally {
      updateTaskUploadItemStatus(taskUploadId)
    }
  }

  return { onUploadFileItem }
}
